import { useEffect, useRef, useState } from "react";
import { Loader, Maximize, Pause, Play } from "lucide-react";

const fmt = (s) => {
    if (!Number.isFinite(s) || s < 0) return "0:00";
    const m = Math.floor(s / 60);
    return `${m}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
};

// Lecteur multimédia RAV : vidéo aux couleurs de l'app (pas les contrôles natifs
// du navigateur). Le GRAND bouton central démarre la lecture directement dans le
// geste du doigt — c'est ce qui autorise la vidéo sur iPhone (un play() lancé
// hors geste est refusé par iOS). Le parent pilote lecture/pause via `playing` ;
// onToggle prévient le parent ; onEnded signale la fin (la séance enchaîne).
export default function VideoPlayer({ src, playing, onToggle, onEnded, testId = "rav-video" }) {
    const videoRef = useRef(null);
    const trackRef = useRef(null);
    const dragging = useRef(false);
    const [isPlaying, setIsPlaying] = useState(false);
    const [time, setTime] = useState(0);
    const [dur, setDur] = useState(0);
    const [waiting, setWaiting] = useState(false);
    const [error, setError] = useState(false);

    // Pilotage lecture/pause depuis le parent (séance : pause globale ; boutique/kids : carte active)
    useEffect(() => {
        const v = videoRef.current;
        if (!v) return;
        if (playing) v.play().catch(() => {}); // refusé hors geste → le bouton central prend le relais
        else v.pause();
    }, [playing, src]);

    // Lecture/pause DIRECTEMENT dans le geste (règle iOS), puis on prévient le parent
    const handleToggle = () => {
        const v = videoRef.current;
        if (!v) return;
        if (v.paused) v.play().catch(() => {});
        else v.pause();
        onToggle?.();
    };

    const seekToClientX = (clientX) => {
        const v = videoRef.current;
        if (!v || !Number.isFinite(v.duration) || !trackRef.current) return;
        const rect = trackRef.current.getBoundingClientRect();
        const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
        try {
            if (ratio >= 0.97) {
                // Curseur mené au bout : la vidéo est considérée comme terminée,
                // l'événement « ended » enchaîne sur la suite (jamais de redémarrage)
                v.currentTime = v.duration;
                return;
            }
            v.currentTime = ratio * v.duration;
            setTime(v.currentTime);
        } catch {
            // Flux pas encore cherchable : on ignore, la lecture continue
        }
    };

    const onPointerDown = (e) => {
        dragging.current = true;
        e.currentTarget.setPointerCapture?.(e.pointerId);
        seekToClientX(e.clientX);
    };
    const onPointerMove = (e) => {
        if (dragging.current) seekToClientX(e.clientX);
    };
    const onPointerUp = () => {
        dragging.current = false;
    };

    const fullscreen = () => {
        const v = videoRef.current;
        if (!v) return;
        if (v.requestFullscreen) v.requestFullscreen().catch(() => {});
        else if (v.webkitEnterFullscreen) v.webkitEnterFullscreen(); // iPhone Safari
        else if (v.webkitRequestFullscreen) v.webkitRequestFullscreen();
    };

    const pct = dur ? Math.min(100, (time / dur) * 100) : 0;

    return (
        <div className="rav-video" data-testid={testId}>
            <video
                ref={videoRef}
                src={src}
                playsInline
                preload="metadata"
                data-testid={`${testId}-element`}
                onClick={handleToggle}
                onPlay={() => {
                    setIsPlaying(true);
                    window.__ravVideoPlaying = true; // témoin de vérification
                }}
                onPause={() => {
                    setIsPlaying(false);
                    window.__ravVideoPlaying = false;
                }}
                onTimeUpdate={(e) => setTime(e.currentTarget.currentTime)}
                onLoadedMetadata={(e) => setDur(e.currentTarget.duration)}
                onWaiting={() => setWaiting(true)}
                onPlaying={() => setWaiting(false)}
                onEnded={() => {
                    setIsPlaying(false);
                    onEnded?.();
                }}
                onError={() => setError(true)}
            />
            {!isPlaying && !error && (
                <button
                    type="button"
                    className="rav-video-overlay"
                    data-testid={`${testId}-bigplay`}
                    aria-label="Lire la vidéo"
                    onClick={handleToggle}
                >
                    <span className="rav-video-bigbtn">
                        <Play size={30} strokeWidth={1.6} />
                    </span>
                </button>
            )}
            {waiting && !error && (
                <span className="rav-video-loading" data-testid={`${testId}-loading`}>
                    <Loader size={20} strokeWidth={1.8} className="rav-spin" />
                    Chargement…
                </span>
            )}
            {error && (
                <p className="rav-video-error" data-testid={`${testId}-error`}>
                    Cette vidéo ne peut pas être lue sur cet appareil.
                </p>
            )}
            <div className="rav-video-bar">
                <button
                    type="button"
                    className="rav-player-btn rav-player-toggle rav-video-toggle"
                    data-testid={`${testId}-toggle`}
                    aria-label={isPlaying ? "Mettre en pause" : "Lire la vidéo"}
                    onClick={handleToggle}
                >
                    {isPlaying ? <Pause size={17} strokeWidth={1.8} /> : <Play size={17} strokeWidth={1.8} />}
                </button>
                <span className="rav-player-time" data-testid={`${testId}-time-current`}>{fmt(time)}</span>
                <div
                    ref={trackRef}
                    className="rav-player-track"
                    data-testid={`${testId}-progress`}
                    role="slider"
                    aria-label="Progression de la vidéo"
                    aria-valuemin={0}
                    aria-valuemax={Math.round(dur)}
                    aria-valuenow={Math.round(time)}
                    onPointerDown={onPointerDown}
                    onPointerMove={onPointerMove}
                    onPointerUp={onPointerUp}
                    onPointerCancel={onPointerUp}
                >
                    <div className="rav-player-fill" style={{ width: `${pct}%` }} />
                    <div className="rav-player-thumb" style={{ left: `${pct}%` }} />
                </div>
                <span className="rav-player-time" data-testid={`${testId}-time-total`}>{fmt(dur)}</span>
                <button
                    type="button"
                    className="rav-player-btn rav-video-fullscreen"
                    data-testid={`${testId}-fullscreen`}
                    aria-label="Plein écran"
                    onClick={fullscreen}
                >
                    <Maximize size={15} strokeWidth={1.8} />
                </button>
            </div>
        </div>
    );
}
